import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { Pagination } from 'semantic-ui-react';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import paginationLeftSVG from '@plone/volto/icons/left-key.svg';
import paginationRightSVG from '@plone/volto/icons/right-key.svg';

import CampoBusca from './CampoBusca';
import {
  ORDENS,
  TAMANHO,
  TODAS_AS_ABAS,
  TUDO,
  decodificar,
  metaDoItem,
  numero,
} from './busca';

const rotuloDoTotal = (n) =>
  n === 0 ? 'Nenhum resultado' : `${numero(n)} resultado${n === 1 ? '' : 's'}`;

// Página de resultados no DS: banner, campo de busca, abas por tipo com o
// total de cada uma, ordenação, lista no desenho da Lista DS e paginação.
const ResultadosBusca = ({
  termo,
  assunto,
  aba,
  ordem,
  pagina,
  total,
  contagens,
  itens,
  carregando,
  secoes,
  irPara,
}) => {
  const paginas = Math.ceil((total || 0) / TAMANHO);
  const ir = (mudancas) =>
    irPara({ termo, assunto, aba, ordem, pagina: 1, ...mudancas });
  // Busca por termo, por assunto (etiqueta da notícia) ou pelos dois.
  const ativa = !!(termo || assunto);
  const procurado = termo || assunto;
  const rotulo = assunto
    ? termo
      ? `Assunto: ${assunto}`
      : 'Assunto'
    : 'Busca';
  const totalGeral =
    contagens[TUDO.id] ?? (aba === TUDO.id && !carregando ? total : null);

  return (
    <div id="page-search" className="ag-busca">
      <header className="ag-page-banner ag-page-banner--blue ag-page-banner--left">
        <span className="ag-page-banner__watermark" aria-hidden="true" />
        <div className="ag-page-banner__inner">
          <span className="ag-page-banner__eyebrow">{rotulo}</span>
          <h1 className="ag-page-banner__title">
            {termo ? `Resultados para “${termo}”` : assunto || 'Busca'}
          </h1>
          {ativa && totalGeral !== null && (
            <p className="ag-page-banner__lede">{rotuloDoTotal(totalGeral)}</p>
          )}
        </div>
      </header>

      <div className="ag-busca__corpo">
        <div className="ag-lista">
          <div className="ag-lista__panel">
            <div className="ag-lista__search">
              <CampoBusca
                valor={termo}
                onBuscar={(t) => ir({ termo: t, assunto: '', aba: TUDO.id })}
              />
            </div>

            {ativa && (
              <div className="ag-busca__filtros">
                <div
                  className="ag-busca__abas"
                  role="group"
                  aria-label="Tipo de conteúdo"
                >
                  {TODAS_AS_ABAS.map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      className={cx('ag-busca__aba', {
                        'is-active': a.id === aba,
                      })}
                      aria-pressed={a.id === aba}
                      onClick={() => ir({ aba: a.id })}
                    >
                      {a.rotulo}
                      {contagens[a.id] !== null &&
                        contagens[a.id] !== undefined && (
                          <span className="ag-busca__n">
                            {' '}
                            ({numero(contagens[a.id])})
                          </span>
                        )}
                    </button>
                  ))}
                </div>
                <div
                  className="ag-busca__ordem"
                  role="group"
                  aria-label="Ordenar"
                >
                  <span className="ag-busca__ordem-rotulo" aria-hidden="true">
                    Ordenar:
                  </span>
                  {ORDENS.map((o) => (
                    <button
                      key={o.id}
                      type="button"
                      className="ag-busca__ordem-btn"
                      aria-pressed={o.id === ordem}
                      onClick={() => ir({ ordem: o.id })}
                    >
                      {o.rotulo}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {ativa && itens.length > 0 && (
              <ul className="ag-lista__list" aria-busy={carregando}>
                {itens.map((item) => (
                  <li key={item['@id']} className="ag-lista__item">
                    <UniversalLink item={item}>
                      <span className="ag-lista__item-main">
                        <strong className="ag-lista__item-title">
                          {decodificar(item.title || item.id)}
                        </strong>
                        {item.description && (
                          <small className="ag-lista__item-description">
                            {decodificar(item.description)}
                          </small>
                        )}
                      </span>
                      <span className="ag-lista__item-meta">
                        <span className="ag-lista__item-date">
                          {metaDoItem(item, secoes)}
                        </span>
                      </span>
                    </UniversalLink>
                  </li>
                ))}
              </ul>
            )}

            {ativa && itens.length === 0 && (
              <p className="ag-busca__aviso" role="status">
                {carregando
                  ? 'Buscando…'
                  : `Nenhum resultado para “${procurado}”${
                      aba === TUDO.id ? '' : ' nesta aba'
                    }. Tente outra palavra${
                      aba === TUDO.id ? '' : ' ou veja a aba Tudo'
                    }.`}
              </p>
            )}
            {!ativa && (
              <p className="ag-busca__aviso">
                Digite no campo acima o que você procura.
              </p>
            )}

            {ativa && paginas > 1 && (
              <nav
                className="ag-busca__paginacao"
                aria-label="Páginas de resultados"
              >
                <Pagination
                  activePage={pagina}
                  totalPages={paginas}
                  onPageChange={(e, { activePage }) =>
                    irPara({ termo, assunto, aba, ordem, pagina: activePage })
                  }
                  firstItem={null}
                  lastItem={null}
                  // o Semantic desenha os itens como <a> sem href, que não têm
                  // papel próprio: sem role, o aria-label é proibido e o leitor
                  // de tela não os anuncia como controle
                  pageItem={{ role: 'button' }}
                  prevItem={{
                    role: 'button',
                    content: <Icon name={paginationLeftSVG} size="18px" />,
                    icon: true,
                    'aria-label': 'Página anterior',
                    'aria-disabled': pagina <= 1,
                    className: pagina <= 1 ? 'disabled' : null,
                  }}
                  nextItem={{
                    role: 'button',
                    content: <Icon name={paginationRightSVG} size="18px" />,
                    icon: true,
                    'aria-label': 'Próxima página',
                    'aria-disabled': pagina >= paginas,
                    className: pagina >= paginas ? 'disabled' : null,
                  }}
                />
              </nav>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

ResultadosBusca.propTypes = {
  termo: PropTypes.string,
  assunto: PropTypes.string,
  aba: PropTypes.string.isRequired,
  ordem: PropTypes.string.isRequired,
  pagina: PropTypes.number.isRequired,
  total: PropTypes.number,
  contagens: PropTypes.objectOf(PropTypes.number),
  itens: PropTypes.arrayOf(PropTypes.object),
  carregando: PropTypes.bool,
  secoes: PropTypes.arrayOf(PropTypes.object),
  irPara: PropTypes.func.isRequired,
};

ResultadosBusca.defaultProps = {
  termo: '',
  assunto: '',
  total: 0,
  contagens: {},
  itens: [],
  carregando: false,
  secoes: [],
};

export default ResultadosBusca;
